import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
  type OnChangeFn,
  type SortingState,
} from '@tanstack/react-table';
import { ArrowUp, ArrowDown, ChevronsUpDown, Loader2 } from 'lucide-react';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
  TableCell,
  Checkbox,
} from '@roger/ui-components';
import TablePaginationFooter from '../TablePaginationFooter';

import type { BillingTableQuery } from '../../hooks/useBillingTableQuery';
import { BillingTableToolbar } from './BillingTableToolbar';

/**
 * The one spinner-and-label the table uses for both of its waiting states — the
 * cold load and the background refetch. Both replace the rows with this, so the
 * two states are indistinguishable apart from their wording.
 */
function TableLoadingLabel({ label }: { label: string }) {
  return (
    <span
      role='status'
      aria-live='polite'
      className='flex items-center justify-center gap-1.5 text-sm text-muted-foreground'
    >
      <Loader2
        className='h-4 w-4 animate-spin motion-reduce:animate-none'
        aria-hidden='true'
      />
      {label}
    </span>
  );
}

/** Wait this long before showing the refresh indicator. A refetch that finishes
 *  faster than this reads as instantaneous, and flashing a spinner for a few
 *  frames is worse than showing nothing. */
const REFRESH_SHOW_DELAY_MS = 300;
/** Once shown, keep it up at least this long so it cannot blink. */
const REFRESH_MIN_VISIBLE_MS = 500;

/**
 * Debounced visibility for the background-refresh indicator.
 *
 * Server-driven search/sort/filter/paging refetch on every change, so the raw
 * `isRefreshing` flag toggles far too eagerly to render directly. This hides
 * fetches that resolve inside the delay window entirely, and guarantees a
 * minimum on-screen time for the ones that don't.
 *
 * The search box is already debounced upstream, and that debounce elapses
 * before the request starts — so these timers measure the request itself and
 * the two never compound.
 */
function useRefreshIndicator(isRefreshing: boolean): boolean {
  const [visible, setVisible] = useState(false);
  const shownAtRef = useRef(0);

  useEffect(() => {
    if (isRefreshing) {
      // Already up: leave it alone. Without this guard a back-to-back refetch
      // would re-arm the delay and keep pushing `shownAtRef` forward.
      if (visible) return;
      const timer = setTimeout(() => {
        shownAtRef.current = Date.now();
        setVisible(true);
      }, REFRESH_SHOW_DELAY_MS);
      return () => clearTimeout(timer);
    }

    if (!visible) return;
    const remaining =
      REFRESH_MIN_VISIBLE_MS - (Date.now() - shownAtRef.current);
    if (remaining <= 0) {
      setVisible(false);
      return;
    }
    const timer = setTimeout(() => setVisible(false), remaining);
    return () => clearTimeout(timer);
  }, [isRefreshing, visible]);

  return visible;
}

export interface BillingSelection {
  selectedIds: Set<string>;
  onToggleId: (id: string) => void;
  onToggleAllVisible: (visibleIds: string[]) => void;
}

/**
 * The slice of a server query state the table body/footer needs. Both the claims
 * `BillingTableQuery` and the `RemittanceTableQuery` satisfy it structurally, so
 * neither hook file has to change.
 *
 * Deliberately excludes `search`/`setSearch`/`reset`: the table never reads them,
 * each caller's toolbar owns them.
 */
export interface ServerTableQuery {
  sorting: SortingState;
  /** 0-based page index (tanstack convention). */
  page: number;
  perPage: number;
  /** Single-sort: accepts a value or a tanstack updater. */
  setSorting: OnChangeFn<SortingState>;
  setPage: (pageIndex: number) => void;
  setPerPage: (perPage: number) => void;
}

interface ServerDataTableProps<T> {
  /** One server-returned page of rows. */
  data: T[];
  columns: ColumnDef<T>[];
  getRowId: (row: T) => string;
  /** Server-driven query state (sorting/paging owned by the caller). */
  query: ServerTableQuery;
  /** Server total row count for the current filter. */
  total: number;
  /** Server total page count for the current filter. */
  totalPages: number;
  /** Whether the current server request is loading; toolbar/pager remain mounted. */
  isLoading?: boolean;
  /**
   * True while a background refetch replaces rows that are still on screen
   * (`isFetching && !isPending`) — a filter, sort, page or search change.
   *
   * Distinct from `isLoading`, which blanks the table because there is nothing
   * to show yet. Here the previous rows stay readable and this only drives the
   * subtle "Updating..." indicator.
   */
  isRefreshing?: boolean;
  selection?: BillingSelection;
  pageSizeOptions?: number[];
  /** Search/filter/reset row rendered above the table. Callers own their controls. */
  toolbar: React.ReactNode;
  /** Shown when the server returns zero rows. */
  emptyMessage?: string;
  /** Makes rows clickable. Cells with their own click MUST stopPropagation. */
  onRowClick?: (row: T) => void;
}

export function ServerDataTable<T>({
  data,
  columns,
  getRowId,
  query,
  total,
  totalPages,
  isLoading = false,
  isRefreshing = false,
  selection,
  pageSizeOptions = [10, 25, 50],
  toolbar,
  emptyMessage = 'No claims match your filters',
  onRowClick,
}: ServerDataTableProps<T>) {
  const showRefreshing = useRefreshIndicator(isRefreshing);

  // The rows are replaced by whichever wait is in effect. The cold load wins
  // when both are true, since "Loading..." is the more accurate word for a
  // table that has never had rows.
  const waitingLabel = isLoading
    ? 'Loading...'
    : showRefreshing
      ? 'Updating...'
      : null;

  // Optional leading selection column.
  const allColumns = useMemo<ColumnDef<T>[]>(() => {
    if (!selection) return columns;
    const selCol: ColumnDef<T> = {
      id: '__select__',
      enableSorting: false,
      header: ({ table }) => {
        // Server already returned only the current page, so select-all toggles
        // just the current page's rows. Selections made on other pages persist
        // (see useBillingCycles) and are intentionally left untouched here.
        const pageRows = table.getRowModel().rows;
        const visibleIds = pageRows.map(r => r.id);
        const selectedVisible = visibleIds.filter(id =>
          selection.selectedIds.has(id)
        ).length;
        const allSelected =
          visibleIds.length > 0 && selectedVisible === visibleIds.length;
        const checked = allSelected
          ? true
          : selectedVisible > 0
            ? 'indeterminate'
            : false;
        return (
          <Checkbox
            aria-label='Select all'
            checked={checked}
            onCheckedChange={() => {
              const ids = table.getRowModel().rows.map(r => r.id);
              selection.onToggleAllVisible(ids);
            }}
          />
        );
      },
      cell: ({ row }) => (
        <Checkbox
          aria-label={`Select row ${row.id}`}
          checked={selection.selectedIds.has(row.id)}
          onCheckedChange={() => selection.onToggleId(row.id)}
        />
      ),
    };
    return [selCol, ...columns];
  }, [columns, selection]);

  const table = useReactTable<T>({
    data,
    columns: allColumns,
    getRowId,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    enableMultiSort: false,
    pageCount: totalPages,
    state: {
      sorting: query.sorting,
      pagination: { pageIndex: query.page, pageSize: query.perPage },
    },
    onSortingChange: query.setSorting,
    onPaginationChange: updater => {
      const prev = { pageIndex: query.page, pageSize: query.perPage };
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (next.pageSize !== query.perPage) {
        query.setPerPage(next.pageSize);
      } else if (next.pageIndex !== query.page) {
        query.setPage(next.pageIndex);
      }
    },
    getCoreRowModel: getCoreRowModel(),
  });

  // Clamp the page when the result set shrinks (e.g. a bulk status change empties
  // the current page) so we don't request/show an out-of-range page that reads as
  // "no matches" while earlier pages still have rows. setPage is a stable callback.
  useEffect(() => {
    const lastPage = Math.max(0, totalPages - 1);
    if (query.page > lastPage) {
      query.setPage(lastPage);
    }
  }, [totalPages, query.page, query.setPage]);

  const pageRows = table.getRowModel().rows;
  const colSpan = allColumns.length;

  return (
    <div className='flex flex-col gap-3'>
      {toolbar}

      <div
        className='overflow-x-auto rounded-md border border-border'
        aria-busy={isRefreshing}
      >
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map(hg => (
              <TableRow key={hg.id}>
                {hg.headers.map(header => {
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder ? null : canSort ? (
                        <button
                          type='button'
                          className='flex items-center font-medium hover:text-foreground'
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {sorted === 'asc' ? (
                            <ArrowUp className='ml-1 h-3 w-3' />
                          ) : sorted === 'desc' ? (
                            <ArrowDown className='ml-1 h-3 w-3' />
                          ) : (
                            <ChevronsUpDown className='ml-1 h-3 w-3 opacity-40' />
                          )}
                        </button>
                      ) : (
                        flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {waitingLabel ? (
              <TableRow>
                <TableCell colSpan={colSpan} className='py-10'>
                  <TableLoadingLabel label={waitingLabel} />
                </TableCell>
              </TableRow>
            ) : pageRows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={colSpan}
                  className='py-10 text-center text-muted-foreground'
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              pageRows.map((row, i) => (
                <TableRow
                  key={row.id}
                  className={
                    (i % 2 === 1
                      ? 'bg-muted hover:bg-muted'
                      : 'bg-background hover:bg-background') +
                    (onRowClick ? ' cursor-pointer' : '')
                  }
                  onClick={
                    onRowClick ? () => onRowClick(row.original) : undefined
                  }
                >
                  {row.getVisibleCells().map(cell => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <TablePaginationFooter
        pageIndex={query.page}
        pageCount={totalPages}
        totalCount={total}
        pageSize={query.perPage}
        pageSizeOptions={pageSizeOptions}
        canPreviousPage={table.getCanPreviousPage()}
        canNextPage={table.getCanNextPage()}
        onPageSizeChange={size => table.setPageSize(size)}
        onPreviousPage={() => table.previousPage()}
        onNextPage={() => table.nextPage()}
      />
    </div>
  );
}

type BillingDataTableProps<T> = Omit<
  ServerDataTableProps<T>,
  'toolbar' | 'query' | 'emptyMessage' | 'onRowClick'
> & { query: BillingTableQuery };

/**
 * Claims-side table: `ServerDataTable` pre-wired with the insurance/clinic
 * toolbar. All 7 EOE tabs render this.
 */
export function BillingDataTable<T>({
  query,
  ...rest
}: BillingDataTableProps<T>) {
  return (
    <ServerDataTable
      {...rest}
      query={query}
      toolbar={<BillingTableToolbar query={query} onReset={query.reset} />}
    />
  );
}
